import {
  Cancel,
  CheckCircle,
  Error,
  Info,
  MarkEmailRead,
  Notifications,
  Payment,
  RestaurantMenu,
  ShoppingCart,
  TableRestaurant,
  Update,
} from '../icons';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import notificationService from '../../services/notificationService';
import OrderDetailDialog from '../order/OrderDetailDialog';

const ORDER_NOTIFICATION_TYPES = [
  'order_created',
  'order_updated',
];

const isToday = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

const iconComponents = {
  ShoppingCart,
  Update,
  CheckCircle,
  Cancel,
  Payment,
  Error,
  Info,
  TableRestaurant,
  RestaurantMenu,
  Notifications
};

const NotificationDropdown = ({
  anchorEl,
  open,
  onClose,
  onNotificationRead,
  onUnreadCountChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications({
        page: 1,
        limit: 50,
        unread_only: true,
      });

      const formattedNotifications = notificationService.formatNotifications(
        response.notifications
      );

      const todayPendingOrders = formattedNotifications.filter(
        (n) =>
          ORDER_NOTIFICATION_TYPES.includes(n.notification_type) &&
          isToday(n.created_at)
      );

      setNotifications(todayPendingOrders);

      const count = todayPendingOrders.filter((n) => !n.is_read).length;
      setUnreadCount(count);

      if (onUnreadCountChange) {
        onUnreadCountChange(count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId, event) => {
    event.stopPropagation();

    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );

      // Update unread count
      const newUnreadCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newUnreadCount);

      // Notify parent components
      if (onNotificationRead) {
        onNotificationRead(notificationId);
      }
      if (onUnreadCountChange) {
        onUnreadCountChange(newUnreadCount);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [unreadCount, onNotificationRead, onUnreadCountChange]);

  const handleNotificationClick = useCallback(async (notification) => {
    const orderId = notification.data?.order_id;

    if (!notification.is_read) {
      try {
        await notificationService.markAsRead(notification.id);

        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );

        const newUnreadCount = Math.max(0, unreadCount - 1);
        setUnreadCount(newUnreadCount);

        if (onNotificationRead) {
          onNotificationRead(notification.id);
        }
        if (onUnreadCountChange) {
          onUnreadCountChange(newUnreadCount);
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    onClose();

    if (orderId) {
      setSelectedOrderId(orderId);
      setOrderDialogOpen(true);
    }
  }, [unreadCount, onNotificationRead, onUnreadCountChange, onClose]);

  // Get notification icon component
  const getNotificationIcon = (iconName, color) => {
    const IconComponent = iconComponents[iconName] || Notifications;
    return <IconComponent color={color} fontSize="small" />;
  };

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  return (
    <>
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        sx: {
          width: isMobile ? 'calc(100vw - 32px)' : 380,
          maxWidth: isMobile ? '100%' : 380,
          maxHeight: 500,
          mt: 1
        },
      }}
    >
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Pending Orders Today
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              color="error"
              sx={{ ml: 1, fontSize: '0.75rem' }}
            />
          )}
        </Typography>
      </Box>

      {/* Loading state */}
      {loading && notifications.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && notifications.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No pending orders today
          </Typography>
        </Box>
      )}

      {/* Notifications list */}
      {notifications.map((notification, index) => (
        <React.Fragment key={notification.id}>
          <MenuItem
            onClick={() => handleNotificationClick(notification)}
            sx={{
              py: 1.5,
              px: 2,
              cursor: 'pointer',
              backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
              '&:hover': {
                backgroundColor: 'action.selected',
              },
              borderLeft: notification.is_read ? 'none' : `3px solid ${theme.palette.primary.main}`,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {getNotificationIcon(notification.icon, notification.color)}
            </ListItemIcon>

            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: notification.is_read ? 'normal' : 'bold',
                      flex: 1
                    }}
                  >
                    {notification.title}
                  </Typography>
                  {!notification.is_read && (
                    <Tooltip title="Mark as read">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        sx={{ p: 0.5 }}
                      >
                        <MarkEmailRead fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {notification.formattedTime}
                  </Typography>
                </Box>
              }
            />
          </MenuItem>
          {index < notifications.length - 1 && <Divider />}
        </React.Fragment>
      ))}

    </Menu>

      <OrderDetailDialog
        open={orderDialogOpen}
        onClose={() => {
          setOrderDialogOpen(false);
          setSelectedOrderId(null);
        }}
        orderId={selectedOrderId}
      />
    </>
  );
};

export default NotificationDropdown;
